import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Input,
  List,
  Circle,
  IconButton,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { ChecklistContext } from "providers/checklist";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import ChecklistItem from "components/loans/LoanChecklist/ChecklistItem";
import CategoryLabel from "./CategoryLabel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReorderItems from "./ReorderItems";

interface CategoryListProps {
  filter: string;
  list: any[];
  setList: (list: any[]) => void;
  canEdit: boolean;
  canAdd: boolean;
}

const CategoryList = ({ filter, list, setList, canEdit, canAdd }: CategoryListProps) => {
  const { reorder, loan } = useContext(ChecklistContext);
  const { showSuccess, showError } = useContext(AlertsContext);
  const { user } = useTenant();
  const [newItem, setNewItem] = useState("");
  const [showAddItemInput, setShowAddItemInput] = useState<string | null>(null);

  const addNewItem = async (category: string) => {
    if (newItem.trim() !== "") {
      try {
        const { loan_item } = await apiRequest("/supabase/loan_items/insert", {
          title: newItem,
          category,
          loan: loan?.id,
          type: "checklist",
        });

        setList(
          list.map(item =>
            item.category === category
              ? {
                  ...item,
                  items: [...item.items, loan_item],
                }
              : item
          )
        );
        showSuccess({
          title: "Item Added",
        });
        setNewItem("");
        setShowAddItemInput(null);
      } catch (err) {
        showError();
      }
    }
  };

  const showButtons = (category: string) => {
    setShowAddItemInput(category);
  };

  if (reorder) {
    return <ReorderItems list={list} setList={setList} />;
  }

  return (
    <Box>
      <Accordion defaultIndex={[0]} allowMultiple>
        {list.map(category => (
          <AccordionItem key={category.category} border="none">
            <CategoryLabel category={category.category} />
            <AccordionPanel px="0px">
              <List>
                {category.items
                  .filter((item: any) => item.title.toLowerCase().includes(filter.toLowerCase()))
                  .map((item: any) => (
                    <ChecklistItem key={item.id} item={item} canEdit={canEdit} />
                  ))}
              </List>
              {user.type === "lender" && (
                <Box>
                  <Button
                    variant="ghost"
                    color="gray.400"
                    size="sm"
                    pl="0px"
                    mt="10px"
                    onClick={() => showButtons(category.category)}
                    leftIcon={
                      <IconButton
                        as="span"
                        aria-label="add-new-item"
                        size="xs"
                        variant="ghost"
                        color="gray.300"
                        onClick={() => showButtons(category.category)}
                        isDisabled={!canAdd}
                        icon={
                          <Circle size="20px" borderWidth="2px" borderColor="#E2DFE8">
                            <FontAwesomeIcon icon={["far", "check"]} color="#CBD5E0" />
                          </Circle>
                        }
                      />
                    }
                    isDisabled={!canAdd}
                    fontWeight="400"
                    fontSize="14px"
                  >
                    Add new item...
                  </Button>

                  {showAddItemInput === category.category && (
                    <Box>
                      <Input
                        variant="primary"
                        placeholder="New Item..."
                        size="sm"
                        h="35px"
                        w="400px"
                        value={newItem}
                        onChange={ev => setNewItem(ev.target.value)}
                      />
                      <Button
                        onClick={() => addNewItem(category.category)}
                        px="12px"
                        h="35px"
                        size="sm"
                        w="80px"
                        variant={newItem ? "solid" : "secondary"}
                        ml="18px"
                      >
                        Add
                      </Button>
                      <Button
                        onClick={() => {
                          setNewItem("");
                          setShowAddItemInput(null);
                        }}
                        px="12px"
                        h="35px"
                        size="sm"
                        w="80px"
                        variant="ghost"
                        color="gray.500"
                        ml="8px"
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export default CategoryList;
